import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Category, CloudUpload } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import apiService from '../services/apiService';
import config from '../config/config';

const MainServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching main services...');
      const response = await apiService.getMainServices();
      console.log('Main services response:', response);

      if (response.success) {
        setServices(response.data);
        console.log('Main services loaded:', response.data.length);
      } else {
        console.error('Failed to load main services:', response);
        setError(response.message || 'Failed to load main services');
      }
    } catch (err) {
      console.error('Main services error:', err);
      console.error('Error details:', err.response || err.message || err);
      setError(`Failed to load main services: ${err.response?.data?.message || err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get correct image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // If path already includes 'uploads/', remove it since UPLOADS_BASE_URL already has /uploads
    const cleanPath = imagePath.startsWith('uploads/')
      ? imagePath.substring(8) // Remove 'uploads/'
      : imagePath;
    return `${config.UPLOADS_BASE_URL}/${cleanPath}`;
  };

  const handleAdd = () => {
    setEditMode(false);
    setSelectedService(null);
    setImagePreview(null);
    setOpenDialog(true);
  };

  const handleEdit = (service) => {
    console.log('Editing service:', service);
    console.log('Service image:', service.image);
    setEditMode(true);
    setSelectedService(service);
    // Construct full image URL from filename using helper function
    const imageUrl = getImageUrl(service.image);
    console.log('Image URL:', imageUrl);
    setImagePreview(imageUrl);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this main service?')) {
      return;
    }

    try {
      const response = await apiService.deleteMainService(id);

      if (response.success) {
        alert('Main service deleted successfully');
        fetchServices();
      } else {
        alert(response.message || 'Failed to delete main service');
      }
    } catch (err) {
      alert('Failed to delete main service');
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      console.log('Submitting main service:', values);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('service_type', values.service_type);
      formData.append('description', values.description);
      formData.append('status', values.status);

      if (values.image && values.image instanceof File) {
        formData.append('image', values.image);
      }

      let response;
      if (editMode) {
        response = await apiService.updateMainService(selectedService.id, formData);
      } else {
        response = await apiService.addMainService(formData);
      }

      console.log('Main service response:', response);

      if (response.success) {
        alert(`Main service ${editMode ? 'updated' : 'created'} successfully`);
        setOpenDialog(false);
        setImagePreview(null);
        fetchServices();
      } else {
        console.error('Operation failed with response:', response);
        alert(response.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Main service operation error:', err);
      console.error('Error details:', err.response || err.message || err);
      alert(`Operation failed: ${err.response?.data?.message || err.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object({
    service_type: Yup.string().required('Service type is required'),
    description: Yup.string().required('Description is required'),
    status: Yup.string().required('Status is required'),
  });

  const columns = [
    {
      field: 'image',
      headerName: 'Image',
      width: 100,
      renderCell: (params) => {
        if (!params.row.image) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }

        const imageUrl = getImageUrl(params.row.image);
        console.log('Table image URL:', imageUrl);

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              py: 1
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={params.row.service_type}
              sx={{
                width: 60,
                height: 60,
                objectFit: 'cover',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
              }}
              onError={(e) => {
                console.error('Table image failed to load:', imageUrl);
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect fill="%23f0f0f0" width="60" height="60"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'service_type',
      headerName: 'Service Type',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Category color="primary" />
          <Typography variant="body2" fontWeight={600}>
            {params.row.service_type}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) =>
        params.row.status === 'active' ? (
          <Chip label="Active" color="success" size="small" />
        ) : (
          <Chip label="Inactive" color="default" size="small" />
        ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      renderCell: (params) =>
        params.row.created_at
          ? new Date(params.row.created_at).toLocaleString()
          : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Main Services
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage main service types (e.g., Regular Ride, Outstation)
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Add Main Service
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={services}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            rowHeight={70}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Main Service Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Main Service</DialogTitle>
        <Formik
          initialValues={{
            service_type: selectedService?.service_type || '',
            description: selectedService?.description || '',
            status: selectedService?.status || 'active',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="service_type"
                      label="Service Type"
                      value={values.service_type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.service_type && Boolean(errors.service_type)}
                      helperText={touched.service_type && errors.service_type}
                      placeholder="e.g., Regular Ride, Outstation, Rental"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="description"
                      label="Description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      multiline
                      rows={4}
                      placeholder="Describe the main service type..."
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      startIcon={<CloudUpload />}
                      sx={{ height: '56px' }}
                    >
                      Upload Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setFieldValue('image', file);
                            setImagePreview(URL.createObjectURL(file));
                          }
                        }}
                      />
                    </Button>
                    {imagePreview && (
                      <Box sx={{ mt: 2, textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                        <Box
                          component="img"
                          src={imagePreview}
                          alt="Preview"
                          sx={{
                            maxWidth: '100%',
                            maxHeight: 200,
                            borderRadius: 1,
                            border: '2px solid #e0e0e0',
                          }}
                          onError={(e) => {
                            console.error('Preview image failed to load:', imagePreview);
                            console.error('Is blob URL?', imagePreview.includes('blob:'));
                            console.error('Full URL:', imagePreview);
                          }}
                        />
                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                          {imagePreview.includes('blob:') ? 'New image selected' : 'Current image'}
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth error={touched.status && Boolean(errors.status)}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Status"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                      {touched.status && errors.status && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                          {errors.status}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    bgcolor: '#FFC107',
                    color: '#000',
                    '&:hover': {
                      bgcolor: '#FFD54F',
                    },
                  }}
                >
                  {editMode ? 'Update' : 'Create'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default MainServicesPage;
